import { Document, Image, Page, StyleSheet, Text, View } from "@react-pdf/renderer";

const typeLabels = {
  volunteer: "Bénévole",
  staff: "Staff",
  guest: "Invité",
  press: "Presse",
  provider: "Prestataire",
  artist: "Artiste",
  security: "Sécurité",
  vip: "VIP",
  other: "Autre",
};

const HEX_COLOR_REGEX = /^#[0-9a-f]{6}$/i;
const DEFAULT_PRIMARY_COLOR = "#4200ff";
const DEFAULT_SECONDARY_COLOR = "#ff02c7";

const getSafeColor = (value, fallback) =>
  HEX_COLOR_REGEX.test(String(value || "").trim()) ? String(value).trim() : fallback;

const getOrganizationName = (organization) =>
  organization?.organization || organization?.orgName || organization?.name || organization?.displayName || "Comitty";

const getOrganizationImage = (organization) =>
  organization?.logoUrl || organization?.imageUrl || organization?.avatar || organization?.image || "";

const isUsableImageSrc = (value) => /^https?:\/\//i.test(String(value || "")) || /^data:image\//i.test(String(value || ""));

const truncateText = (value, maxLength) => {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  if (!text || text.length <= maxLength) return text;
  return `${text.slice(0, Math.max(0, maxLength - 1)).trim()}…`;
};

const getAccessLabel = (rule) => truncateText(rule?.label || rule?.zone || "Accès organisation", 34);

const styles = StyleSheet.create({
  page: {
    padding: 24,
    backgroundColor: "#eef4ff",
    fontFamily: "Helvetica",
    color: "#10203d",
  },
  customPage: {
    padding: 20,
    backgroundColor: "#f4f7fb",
    fontFamily: "Helvetica",
    color: "#111827",
  },
  card: {
    backgroundColor: "#ffffff",
    borderRadius: 24,
    padding: 22,
    border: "1px solid #d9e4f5",
  },
  customCard: {
    backgroundColor: "#ffffff",
    borderRadius: 24,
    overflow: "hidden",
    border: "1px solid #e5e7eb",
  },
  customHeaderImage: {
    width: "100%",
    height: 128,
    objectFit: "cover",
  },
  customAccent: {
    height: 6,
  },
  customAccentSecondary: {
    height: 2,
  },
  customContent: {
    padding: 20,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 14,
  },
  customEyebrow: {
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 1.2,
    marginBottom: 5,
  },
  customTitle: {
    fontSize: 22,
    fontWeight: 800,
    lineHeight: 1.16,
    color: "#0f172a",
    marginBottom: 7,
  },
  customMessage: {
    fontSize: 10,
    lineHeight: 1.35,
    color: "#111827",
    marginBottom: 12,
  },
  logo: {
    width: 58,
    height: 58,
    objectFit: "contain",
  },
  compactLogo: {
    width: 44,
    height: 44,
    objectFit: "contain",
  },
  eyebrow: {
    fontSize: 10,
    color: "#6b7b97",
    textTransform: "uppercase",
    letterSpacing: 1.2,
  },
  orgName: {
    fontSize: 18,
    fontWeight: 700,
    marginTop: 4,
  },
  main: {
    flexDirection: "row",
    gap: 14,
  },
  identity: {
    flex: 1,
  },
  identityCard: {
    flex: 1,
    borderRadius: 18,
    backgroundColor: "#f8fafc",
    border: "1px solid #e2e8f0",
    padding: 14,
  },
  name: {
    fontSize: 25,
    fontWeight: 800,
    lineHeight: 1.15,
    marginBottom: 10,
  },
  customName: {
    fontSize: 21,
    fontWeight: 800,
    lineHeight: 1.15,
    color: "#0f172a",
    marginBottom: 8,
  },
  pill: {
    alignSelf: "flex-start",
    backgroundColor: "#2f6df6",
    color: "#ffffff",
    borderRadius: 999,
    paddingVertical: 7,
    paddingHorizontal: 12,
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
  },
  customPill: {
    alignSelf: "flex-start",
    color: "#ffffff",
    borderRadius: 999,
    paddingVertical: 6,
    paddingHorizontal: 11,
    fontSize: 9,
    fontWeight: 700,
    textTransform: "uppercase",
  },
  qrBlock: {
    alignItems: "center",
    width: 160,
  },
  customQrBlock: {
    alignItems: "center",
    width: 166,
    padding: 12,
    borderRadius: 18,
    backgroundColor: "#f8fafc",
    border: "1px solid #e2e8f0",
  },
  customQrTitle: {
    fontSize: 9,
    color: "#475569",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.8,
    marginBottom: 7,
    textAlign: "center",
  },
  qr: {
    width: 135,
    height: 135,
  },
  customQr: {
    width: 126,
    height: 126,
    backgroundColor: "#ffffff",
  },
  qrHelp: {
    marginTop: 7,
    fontSize: 8,
    color: "#6b7b97",
    textAlign: "center",
  },
  section: {
    marginTop: 16,
    paddingTop: 11,
    borderTop: "1px solid #e5ecf7",
  },
  sectionTitle: {
    fontSize: 12,
    color: "#6b7b97",
    textTransform: "uppercase",
    letterSpacing: 0.8,
    marginBottom: 7,
  },
  accessGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
  },
  accessPill: {
    backgroundColor: "#f1f5ff",
    border: "1px solid #dbe6fb",
    borderRadius: 10,
    paddingVertical: 6,
    paddingHorizontal: 8,
    fontSize: 9,
    color: "#263958",
  },
  customAccessPill: {
    borderRadius: 10,
    paddingVertical: 6,
    paddingHorizontal: 8,
    fontSize: 9,
    color: "#334155",
  },
  notes: {
    fontSize: 9,
    color: "#3f506c",
    lineHeight: 1.25,
  },
  footer: {
    marginTop: 18,
    fontSize: 9,
    color: "#7c8aa1",
    textAlign: "center",
  },
  customFooter: {
    marginTop: 14,
    padding: 10,
    borderRadius: 14,
    backgroundColor: "#f8fafc",
    border: "1px solid #e2e8f0",
    fontSize: 8,
    color: "#475569",
    lineHeight: 1.25,
  },
  customFooterText: {
    marginTop: 10,
    paddingTop: 9,
    borderTop: "1px solid #e5e7eb",
    fontSize: 8,
    color: "#475569",
    lineHeight: 1.25,
  },
  accessMorePill: {
    backgroundColor: "#eef2ff",
    border: "1px solid #c7d2fe",
    borderRadius: 10,
    paddingVertical: 6,
    paddingHorizontal: 8,
    fontSize: 9,
    color: "#334155",
    fontWeight: 700,
  },
});

function AccreditationPDF({ accreditation, organization, qrCode, emailCustomization }) {
  const fullName = `${accreditation?.firstName || ""} ${accreditation?.lastName || ""}`.trim();
  const accesses = Array.isArray(accreditation?.accessRules) ? accreditation.accessRules : [];
  const displayedAccesses = accesses.slice(0, 8);
  const hiddenAccessCount = Math.max(0, accesses.length - displayedAccesses.length);
  const customization = emailCustomization?.enabled ? emailCustomization : null;
  const primaryColor = getSafeColor(customization?.primaryColor, DEFAULT_PRIMARY_COLOR);
  const secondaryColor = getSafeColor(customization?.secondaryColor, DEFAULT_SECONDARY_COLOR);
  const organizationName = getOrganizationName(organization);
  const headerImageUrl = customization?.headerImageUrl || "";
  const hasHeaderImage = isUsableImageSrc(headerImageUrl);
  const logoUrl = getOrganizationImage(organization);
  const hasLogo = isUsableImageSrc(logoUrl);

  if (customization) {
    return (
      <Document>
        <Page size="A4" style={styles.customPage}>
          <View style={styles.customCard}>
            {hasHeaderImage ? (
              <Image src={headerImageUrl} style={styles.customHeaderImage} />
            ) : null}
            <View style={[styles.customAccent, { backgroundColor: primaryColor }]} />
            <View style={[styles.customAccentSecondary, { backgroundColor: secondaryColor }]} />

            <View style={styles.customContent}>
              <View style={styles.header}>
                <View>
                  <Text style={[styles.customEyebrow, { color: primaryColor }]}>
                    {organizationName}
                  </Text>
                  <Text style={styles.customTitle}>
                    {customization.title || "Votre accréditation"}
                  </Text>
                </View>
                {!hasHeaderImage && hasLogo ? <Image src={logoUrl} style={styles.compactLogo} /> : null}
              </View>

              {customization.message ? (
                <Text style={styles.customMessage}>{truncateText(customization.message, 260)}</Text>
              ) : null}

              <View style={styles.main}>
                <View style={styles.identityCard}>
                  <Text style={styles.customName}>{fullName || "Accrédité"}</Text>
                  <Text style={[styles.customPill, { backgroundColor: primaryColor }]}>
                    {typeLabels[accreditation?.type] || typeLabels.other}
                  </Text>
                </View>
                <View style={styles.customQrBlock}>
                  <Text style={styles.customQrTitle}>QR code d'accréditation</Text>
                  {qrCode && <Image src={qrCode} style={styles.customQr} />}
                  <Text style={styles.qrHelp}>
                    Présentez ce QR code au scanner Comitty.
                  </Text>
                </View>
              </View>

              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Accès autorisés</Text>
                <View style={styles.accessGrid}>
                  {accesses.length ? (
                    displayedAccesses.map((rule, index) => (
                      <Text
                        key={`${rule.label}-${index}`}
                        style={[
                          styles.customAccessPill,
                          { backgroundColor: "#f8fafc", border: `1px solid ${secondaryColor}` },
                        ]}
                      >
                        {getAccessLabel(rule)}
                      </Text>
                    ))
                  ) : (
                    <Text
                      style={[
                        styles.customAccessPill,
                        { backgroundColor: "#f8fafc", border: `1px solid ${secondaryColor}` },
                      ]}
                    >
                      Accès organisation
                    </Text>
                  )}
                  {hiddenAccessCount > 0 ? (
                    <Text style={styles.accessMorePill}>+{hiddenAccessCount} accès</Text>
                  ) : null}
                </View>
              </View>

              {accreditation?.notes ? (
                <View style={styles.section}>
                  <Text style={styles.sectionTitle}>Consignes</Text>
                  <Text style={styles.notes}>{truncateText(accreditation.notes, 180)}</Text>
                </View>
              ) : null}

              <Text style={styles.customFooter}>
                Votre accréditation est personnelle. Elle est également disponible directement dans l'e-mail via son QR code.
              </Text>

              {customization.footerText ? (
                <Text style={styles.customFooterText}>{truncateText(customization.footerText, 160)}</Text>
              ) : null}
            </View>
          </View>
        </Page>
      </Document>
    );
  }

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.card}>
          <View style={styles.header}>
            <View>
              <Text style={styles.eyebrow}>Accréditation Comitty</Text>
              <Text style={styles.orgName}>{organization?.organization || organization?.name || "Comitty"}</Text>
            </View>
            {organization?.image && <Image src={organization.image} style={styles.logo} />}
          </View>

          <View style={styles.main}>
            <View style={styles.identity}>
              <Text style={styles.name}>{fullName || "Accrédité"}</Text>
              <Text style={styles.pill}>{typeLabels[accreditation?.type] || typeLabels.other}</Text>
            </View>
            <View style={styles.qrBlock}>
              {qrCode && <Image src={qrCode} style={styles.qr} />}
              <Text style={styles.qrHelp}>Présentez ce QR code au contrôle accréditation.</Text>
            </View>
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Accès autorisés</Text>
            <View style={styles.accessGrid}>
              {accesses.length ? (
                displayedAccesses.map((rule, index) => (
                  <Text key={`${rule.label}-${index}`} style={styles.accessPill}>
                    {getAccessLabel(rule)}
                  </Text>
                ))
              ) : (
                <Text style={styles.accessPill}>Accès organisation</Text>
              )}
              {hiddenAccessCount > 0 ? (
                <Text style={styles.accessMorePill}>+{hiddenAccessCount} accès</Text>
              ) : null}
            </View>
          </View>

          {accreditation?.notes ? (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Consignes</Text>
              <Text style={styles.notes}>{truncateText(accreditation.notes, 220)}</Text>
            </View>
          ) : null}

          <Text style={styles.footer}>
            Cette accréditation est personnelle. Le QR code ne contient aucune donnée personnelle.
          </Text>
        </View>
      </Page>
    </Document>
  );
}

export default AccreditationPDF;
